import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { adminLogin, setToken } from "@/lib/adminApi";
import { BRAND } from "@/data/content";

export default function AdminLoginPage() {
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (!password.trim()) return;
        setBusy(true);
        setError("");
        try {
            const { data } = await adminLogin(password.trim());
            if (data?.token) {
                setToken(data.token);
                navigate("/admin/dashboard", { replace: true });
            } else {
                setError("Login failed.");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Login failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center px-5 py-16 bg-[hsl(var(--bg))] relative overflow-hidden"
            data-testid="admin-login-page"
        >
            <div className="absolute inset-0 hero-radial pointer-events-none" />
            <div className="absolute inset-0 dot-grid-fine opacity-50 pointer-events-none [mask-image:radial-gradient(ellipse_at_center,#000_30%,transparent_75%)]" />

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full max-w-md"
            >
                <div className="flex flex-col items-center mb-8 gap-3">
                    <img
                        src={BRAND.logoUrl}
                        alt="IdeaClap"
                        className="h-20 w-20 object-contain"
                    />
                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                        Internal · Admin
                    </div>
                </div>

                <div className="rounded-3xl border border-black/[0.08] bg-white shadow-deep p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(187,118,33,0.10)] ring-1 ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]">
                            <Lock className="h-4 w-4" />
                        </span>
                        <div>
                            <h1 className="font-display text-2xl text-[hsl(var(--text))] leading-none">
                                Admin sign in
                            </h1>
                            <p className="text-[12.5px] text-[hsl(var(--text-subtle))] mt-1">
                                Read-only access to leads, messages, and chat history.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
                        <label className="flex flex-col gap-2">
                            <span className="text-[12px] uppercase tracking-[0.16em] font-mono text-[hsl(var(--text-muted))]">
                                Password
                            </span>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (error) setError("");
                                }}
                                autoFocus
                                placeholder="••••••••••••"
                                className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                data-testid="admin-password-input"
                            />
                        </label>
                        {error && (
                            <div className="text-[12.5px] text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/8 rounded-lg px-3 py-2">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={busy || !password.trim()}
                            data-testid="admin-login-submit"
                            className="sheen group inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/92 text-[hsl(var(--bg))] px-5 py-3 text-[14px] font-semibold disabled:opacity-70 transition-colors"
                        >
                            {busy ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Signing in…
                                </>
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="mt-6 text-center text-[11.5px] text-[hsl(var(--text-subtle))]">
                    © {new Date().getFullYear()} IdeaClap Global · Internal
                </p>
            </motion.div>
        </div>
    );
}
